/**
 * useAvatarHealth.ts
 *
 * Aggregates structured health signals from across the avatar stack into
 * one observable surface so:
 *   1. The on-screen <AvatarHealthBadge /> can render a visible status pip
 *      (green / amber / red) on every consumer surface — DM, AgentLink,
 *      Visual IVR, builder live mode.
 *   2. External tooling (ops dashboards, Sentry, Datadog) can subscribe to
 *      a single `avatar:health` window event instead of having to duplicate
 *      the trap-point catalogue.
 *
 * Why this hook exists (the problem we're solving):
 *
 *   Pre-Phase-3 the avatar had ~6 independent failure modes (TURN missing,
 *   WebRTC failed, WebSocket reconnecting, audio access denied, WebGL
 *   context lost, model load failed) and each one logged a console.warn
 *   nobody saw. Users on DM and AgentLink experienced "it just doesn't
 *   work" without any signal that anything was wrong, and we had no
 *   structured telemetry to triage from production logs.
 *
 *   This hook turns those silent failures into one visible pip + one
 *   structured event stream. Layer A is observability (no UI cost). Layer
 *   B (visible badge) is rendered by `<AvatarHealthBadge />`.
 *
 * Severity levels:
 *   - 'ok'   → all signals nominal. Pip is dim green, no popover content.
 *   - 'warn' → degraded but still working (e.g. STUN-only fallback, slow
 *              ICE gather, audio access pending). Pip is amber, popover
 *              lists each warning.
 *   - 'fail' → user-visible failure (e.g. WebGL lost, audio access denied,
 *              voice call failed, WebSocket gave up reconnecting). Pip is
 *              red, popover lists each failure.
 *
 * Window event contract (Layer A — observability):
 *   Whenever the aggregate state changes, this hook dispatches:
 *     window.dispatchEvent(new CustomEvent('avatar:health', {
 *       detail: { overall, issues, lastUpdated }
 *     }))
 *   Consumers can subscribe like:
 *     window.addEventListener('avatar:health', (evt) => sendToSentry(evt.detail))
 *
 * Inputs:
 *   - turnCredentialsState: pass `useTurnCredentials()` return value here.
 *     The hook reads `provider`, `status`, `lastError` to detect STUN-only
 *     fallback / fetch failures.
 *   - liveSessionStatus: pass `liveSessionState?.status` from
 *     `useHostedLiveState`. Catches 'error' and 'ended' from the backend.
 *   - liveConnectionError: pass the same string the live error banner
 *     reads. Catches inline errors that don't bubble through `status`.
 *   - voiceCallSessionState: pass the latest 'failed' / 'connection-lost'
 *     state if you've wired `onSessionStateChange`. Optional — falls
 *     back to listening for `voiceCallFeature` events on `window` if
 *     not provided.
 *   - enabled: turn off the badge entirely (e.g. visual-only on phone
 *     where there's no live mode at all).
 *
 * Trap points (the events / state we listen for):
 *   - Window events:
 *       'avatar:audio-access-required'   → audio playback blocked
 *       'webglcontextlost' (on canvas)    → 3D renderer dead
 *       'webglcontextrestored' (on canvas)→ 3D renderer recovered
 *       'avatar:voice-call-state'        → custom event we dispatch from
 *                                           voiceCallFeature when we don't
 *                                           have a direct handler
 *   - Direct inputs (above): TURN status, live session status, live error
 *
 * What we DON'T trap (intentionally):
 *   - Per-frame WebGL warnings (too noisy, not actionable)
 *   - Network jitter (not a discrete event; if it matters it'll show up
 *     as a voice-call 'failed' or 'connection-lost')
 *   - LLM latency (a 3s reply is annoying but not a health failure)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { AvatarSessionStatus } from '../../session/types'
import type { UseTurnCredentialsState } from './useTurnCredentials'

export type AvatarHealthSeverity = 'ok' | 'warn' | 'fail'

export type AvatarHealthIssueId =
  | 'turn-stun-only'
  | 'turn-fetch-failed'
  | 'voice-call-failed'
  | 'voice-call-connection-lost'
  | 'live-session-error'
  | 'audio-access-required'
  | 'webgl-context-lost'

export interface AvatarHealthIssue {
  id: AvatarHealthIssueId
  severity: 'warn' | 'fail'
  /**
   * Short human-readable label rendered in the popover. Keep it under
   * ~60 chars so it doesn't wrap awkwardly on the small popover.
   */
  label: string
  /**
   * Optional longer detail (e.g. specific error message). Rendered as a
   * second line in the popover.
   */
  detail?: string
  /**
   * When this issue was first observed (epoch ms). Used by the popover
   * to render "5s ago" / "12m ago" relative timestamps.
   */
  firstSeenAt: number
  /**
   * Last time we observed this issue (epoch ms). Updates on repeated
   * triggers so the popover can show whether the issue is fresh or
   * historical.
   */
  lastSeenAt: number
}

export interface AvatarHealthState {
  overall: AvatarHealthSeverity
  issues: AvatarHealthIssue[]
  lastUpdated: number
}

export interface UseAvatarHealthOptions {
  /**
   * Pass the return value of `useTurnCredentials()` here. The hook
   * derives 'turn-stun-only' (warn) and 'turn-fetch-failed' (warn)
   * from this.
   */
  turnCredentialsState?: UseTurnCredentialsState
  /**
   * Current session status from the runtime-service (e.g.
   * `liveSessionState?.status`). 'error' surfaces as a fail issue.
   */
  liveSessionStatus?: AvatarSessionStatus | null
  /**
   * Current inline error string from the live overlay. When non-null
   * it surfaces as a fail issue alongside the status.
   */
  liveConnectionError?: string | null
  /**
   * Disable the hook entirely. The hook still returns a state object
   * so the badge can decide to render nothing.
   */
  enabled?: boolean
}

const INITIAL_STATE: AvatarHealthState = {
  overall: 'ok',
  issues: [],
  lastUpdated: 0,
}

/**
 * Severity ranking helper — used to compute the overall pip colour
 * from the worst issue currently observed.
 */
function worstSeverity(issues: AvatarHealthIssue[]): AvatarHealthSeverity {
  if (issues.some((i) => i.severity === 'fail')) return 'fail'
  if (issues.some((i) => i.severity === 'warn')) return 'warn'
  return 'ok'
}

/**
 * Stable equality check on an issue list. Used to skip re-renders /
 * re-dispatches when the underlying signals churn but produce the same
 * aggregate state.
 */
function issuesEqual(a: AvatarHealthIssue[], b: AvatarHealthIssue[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if (x.id !== y.id || x.severity !== y.severity || x.label !== y.label || x.detail !== y.detail) {
      return false
    }
  }
  return true
}

export function useAvatarHealth({
  turnCredentialsState,
  liveSessionStatus,
  liveConnectionError,
  enabled = true,
}: UseAvatarHealthOptions): AvatarHealthState {
  // The "live" issue map is the source of truth. We mutate it via a
  // setter so React knows when to re-render. Each entry maps issue id
  // → AvatarHealthIssue; we materialise it into an array on every read.
  const [issueMap, setIssueMap] = useState<Map<AvatarHealthIssueId, AvatarHealthIssue>>(() => new Map())

  // Last dispatched state — guards against duplicate `avatar:health`
  // events when the inputs churn but the aggregate hasn't changed.
  const lastDispatchedRef = useRef<AvatarHealthState | null>(null)

  const upsertIssue = useCallback(
    (
      id: AvatarHealthIssueId,
      severity: 'warn' | 'fail',
      label: string,
      detail?: string,
    ) => {
      setIssueMap((prev) => {
        const next = new Map(prev)
        const now = Date.now()
        const existing = next.get(id)
        if (existing && existing.severity === severity && existing.label === label && existing.detail === detail) {
          // Same issue, just bump lastSeenAt without forcing equality
          // failure on every dependent update.
          existing.lastSeenAt = now
          return next
        }
        next.set(id, {
          id,
          severity,
          label,
          detail,
          firstSeenAt: existing?.firstSeenAt ?? now,
          lastSeenAt: now,
        })
        return next
      })
    },
    [],
  )

  const clearIssue = useCallback((id: AvatarHealthIssueId) => {
    setIssueMap((prev) => {
      if (!prev.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  // ---------------------------------------------------------------
  // Trap point 1: TURN credentials. STUN-only fallback is a warn (live
  // mode might still work over open networks). A fetch error after a
  // previous success is also a warn (existing creds may still be valid
  // until expiry). A fetch error with no prior success is silent here
  // because the consumer falls through to STUN-only and we already
  // emit the stun-only warning above.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!enabled || !turnCredentialsState) {
      clearIssue('turn-stun-only')
      clearIssue('turn-fetch-failed')
      return
    }
    if (turnCredentialsState.provider === 'stun-only') {
      upsertIssue(
        'turn-stun-only',
        'warn',
        'Voice using STUN-only (no TURN relay)',
        turnCredentialsState.lastError ?? undefined,
      )
    } else if (turnCredentialsState.provider === 'twilio-nts') {
      clearIssue('turn-stun-only')
    }

    if (turnCredentialsState.status === 'error') {
      upsertIssue(
        'turn-fetch-failed',
        'warn',
        'TURN credential refresh failed',
        turnCredentialsState.lastError ?? undefined,
      )
    } else if (turnCredentialsState.status === 'ready') {
      clearIssue('turn-fetch-failed')
    }
  }, [
    enabled,
    turnCredentialsState?.provider,
    turnCredentialsState?.status,
    turnCredentialsState?.lastError,
    turnCredentialsState,
    upsertIssue,
    clearIssue,
  ])

  // ---------------------------------------------------------------
  // Trap point 2: live session status from the runtime. 'error' is
  // a hard failure (the backend has given up). 'ended' is normal and
  // doesn't surface (it's how a healthy session terminates).
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!enabled) {
      clearIssue('live-session-error')
      return
    }
    if (liveSessionStatus === 'error' || (liveConnectionError && liveConnectionError.trim().length > 0)) {
      upsertIssue(
        'live-session-error',
        'fail',
        'Live session error',
        liveConnectionError ?? undefined,
      )
    } else if (liveSessionStatus === 'ready' || liveSessionStatus === 'idle' || liveSessionStatus === 'listening') {
      // The session is back in a healthy state — clear the issue.
      // We deliberately don't clear on 'connecting' because we want
      // to remember the prior error until we have positive evidence
      // the new connection succeeded.
      clearIssue('live-session-error')
    }
  }, [enabled, liveSessionStatus, liveConnectionError, upsertIssue, clearIssue])

  // ---------------------------------------------------------------
  // Trap point 3: window events. We listen for the cross-cutting
  // signals that don't have a clean React state input.
  //
  //   - 'avatar:audio-access-required' is dispatched by the audio
  //     playback layer when iOS / Safari blocks autoplay. Cleared
  //     when the user grants access (we listen for 'avatar:audio-
  //     access-granted' which the AudioPermissionChip emits on the
  //     successful grant path).
  //
  //   - 'webglcontextlost' / 'webglcontextrestored' are dispatched
  //     directly on the <canvas> by AvatarSceneShell. To catch them
  //     without coupling the badge to the canvas ref, AvatarSceneShell
  //     ALSO dispatches mirrored 'avatar:webgl-context-lost' /
  //     'avatar:webgl-context-restored' window events that this hook
  //     listens for. (The mirror event path is added in this same
  //     PR — see AvatarSceneShell.tsx.)
  //
  //   - 'avatar:voice-call-state' is dispatched when voiceCallFeature
  //     transitions to 'failed' or 'connection-lost'. Same mirror
  //     pattern as WebGL — we don't want every consumer to plumb a
  //     ref through.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      clearIssue('audio-access-required')
      clearIssue('webgl-context-lost')
      clearIssue('voice-call-failed')
      clearIssue('voice-call-connection-lost')
      return
    }

    const handleAudioAccessRequired = (evt: Event) => {
      const detail = (evt as CustomEvent<{ reason?: string }>).detail
      upsertIssue(
        'audio-access-required',
        'fail',
        'Audio playback blocked — tap to grant',
        detail?.reason,
      )
    }
    const handleAudioAccessGranted = () => {
      clearIssue('audio-access-required')
    }
    const handleWebglLost = (evt: Event) => {
      const detail = (evt as CustomEvent<{ count?: number }>).detail
      upsertIssue(
        'webgl-context-lost',
        'fail',
        'Avatar 3D context lost — recovering',
        detail?.count ? `Loss count: ${detail.count}` : undefined,
      )
    }
    const handleWebglRestored = () => {
      clearIssue('webgl-context-lost')
    }
    const handleVoiceCallState = (evt: Event) => {
      const detail = (evt as CustomEvent<{ state?: string; reason?: string }>).detail
      const state = detail?.state
      if (state === 'failed') {
        upsertIssue('voice-call-failed', 'fail', 'Voice call failed', detail?.reason)
      } else if (state === 'connection-lost') {
        upsertIssue(
          'voice-call-connection-lost',
          'fail',
          'Voice call dropped — reconnecting',
          detail?.reason,
        )
      } else if (state === 'confirmed' || state === 'audio-ready' || state === 'accepted') {
        clearIssue('voice-call-failed')
        clearIssue('voice-call-connection-lost')
      } else if (state === 'ended') {
        // 'ended' covers both healthy hangup and post-failure cleanup.
        // Don't clear failures on 'ended' — we want the badge to keep
        // showing the last-known failure so the user understands why
        // the call dropped, until they start a new call.
      }
    }

    window.addEventListener('avatar:audio-access-required', handleAudioAccessRequired as EventListener)
    window.addEventListener('avatar:audio-access-granted', handleAudioAccessGranted as EventListener)
    window.addEventListener('avatar:webgl-context-lost', handleWebglLost as EventListener)
    window.addEventListener('avatar:webgl-context-restored', handleWebglRestored as EventListener)
    window.addEventListener('avatar:voice-call-state', handleVoiceCallState as EventListener)

    return () => {
      window.removeEventListener('avatar:audio-access-required', handleAudioAccessRequired as EventListener)
      window.removeEventListener('avatar:audio-access-granted', handleAudioAccessGranted as EventListener)
      window.removeEventListener('avatar:webgl-context-lost', handleWebglLost as EventListener)
      window.removeEventListener('avatar:webgl-context-restored', handleWebglRestored as EventListener)
      window.removeEventListener('avatar:voice-call-state', handleVoiceCallState as EventListener)
    }
  }, [enabled, upsertIssue, clearIssue])

  // ---------------------------------------------------------------
  // Materialise the aggregate state and dispatch the observability
  // event whenever it changes.
  // ---------------------------------------------------------------
  const state = useMemo<AvatarHealthState>(() => {
    if (!enabled) {
      return INITIAL_STATE
    }
    const issues = Array.from(issueMap.values()).sort((a, b) => {
      // Sort failures first, then warnings, then by recency.
      if (a.severity !== b.severity) {
        return a.severity === 'fail' ? -1 : 1
      }
      return b.lastSeenAt - a.lastSeenAt
    })
    return {
      overall: worstSeverity(issues),
      issues,
      lastUpdated: issues.length > 0 ? Math.max(...issues.map((i) => i.lastSeenAt)) : 0,
    }
  }, [enabled, issueMap])

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    const prev = lastDispatchedRef.current
    if (prev && prev.overall === state.overall && issuesEqual(prev.issues, state.issues)) {
      return
    }
    lastDispatchedRef.current = state
    window.dispatchEvent(new CustomEvent<AvatarHealthState>('avatar:health', { detail: state }))
  }, [enabled, state])

  return state
}

export default useAvatarHealth
